import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Play, Plus, Trash2, Check, Upload, Music4, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import {
  PLACE_SOUNDS, REMOVE_SOUNDS,
  getPlaceSound, setPlaceSound, getRemoveSound, setRemoveSound,
  previewPlace, previewRemove, previewArrival,
  getVolume, setVolume,
  setPlaceCustomUrl, setRemoveCustomUrl,
} from "@/lib/sounds";

function OptionRow({ opt, selected, onSelect, onPreview, onDelete, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onSelect}
      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer transition-colors border ${selected ? "bg-[#2b2620] text-[#fdf6e3] border-[#2b2620]" : "bg-white hover:bg-[#f3ecdf] border-[#e6dcc8]"}`}
    >
      <button
        data-testid={`${testid}-preview`}
        onClick={(e) => { e.stopPropagation(); onPreview(); }}
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${selected ? "bg-[#fdf6e3] text-[#2b2620]" : "bg-[#2b2620] text-[#fdf6e3]"}`}
        title="Preview"
      >
        <Play className="w-3.5 h-3.5" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-sm leading-none truncate">{opt.label}</p>
        <p className={`text-xs mt-0.5 ${selected ? "text-[#d8ccb5]" : "text-[#8c8073]"}`}>{opt.hint}</p>
      </div>
      {onDelete && (
        <button data-testid={`${testid}-delete`} onClick={(e) => { e.stopPropagation(); onDelete(); }} className={`shrink-0 ${selected ? "text-[#d8ccb5] hover:text-white" : "text-[#b7a98f] hover:text-[#ff3b30]"}`} title="Remove custom sound">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      {selected && <Check className="w-4 h-4 shrink-0" />}
    </div>
  );
}

export function SoundSettings({ open, onClose }) {
  const { user, setUser } = useAuth();
  const placeFileRef = useRef(null);
  const removeFileRef = useRef(null);
  const [placeKey, setPlaceKey] = useState(getPlaceSound());
  const [removeKey, setRemoveKey] = useState(getRemoveSound());
  const [busy, setBusy] = useState("");
  const [vol, setVol] = useState(Math.round(getVolume() * 100));
  const onVol = (v) => { const pct = v[0]; setVol(pct); setVolume(pct / 100); };

  const pickPlace = (k) => { setPlaceKey(k); setPlaceSound(k); previewPlace(k); };
  const pickRemove = (k) => { setRemoveKey(k); setRemoveSound(k); previewRemove(k); };

  const upload = async (slot, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/sounds/upload?slot=${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = fileUrl(data.storage_path);
      if (slot === "place") { setPlaceCustomUrl(url); setUser({ ...user, custom_place_sound: data.storage_path }); pickPlace("custom"); }
      else { setRemoveCustomUrl(url); setUser({ ...user, custom_remove_sound: data.storage_path }); pickRemove("custom"); }
      toast.success("Custom sound saved!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setBusy("");
  };

  const removeCustom = async (slot) => {
    try { await api.delete(`/sounds/${slot}`); } catch (err) { logError("Failed to remove custom sound:", err); }
    if (slot === "place") {
      setPlaceCustomUrl(""); setUser({ ...user, custom_place_sound: null });
      if (getPlaceSound() === "custom") pickPlace("peel");
    } else {
      setRemoveCustomUrl(""); setUser({ ...user, custom_remove_sound: null });
      if (getRemoveSound() === "custom") pickRemove("foley");
    }
    toast("Custom sound removed");
  };

  const hasPlaceCustom = !!user?.custom_place_sound;
  const hasRemoveCustom = !!user?.custom_remove_sound;

  const uploadLabel = (slot, hasCustom) => {
    if (busy === slot) return "Uploading…";
    return hasCustom ? "Replace your sound" : "Upload your own";
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sound-settings-dialog" className="max-w-md bg-[#fbf6ec] grain max-h-[85vh] overflow-auto board-scroll">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl text-[#2b2620]">Sound pack</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6b6155] -mt-2">Pick a preset or upload your own. Tap ▶ to preview.</p>

        <div className="mt-3 bg-white/60 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <Volume2 className="w-4 h-4 text-[#a0703c]" />
            <span className="tag-label text-[#a0703c]">Master volume</span>
            <span className="ml-auto text-xs font-bold text-[#6b6155]" data-testid="volume-value">{vol}%</span>
          </div>
          <Slider data-testid="volume-slider" value={[vol]} min={0} max={100} step={5} onValueChange={onVol} onValueCommit={() => previewPlace(placeKey)} />
        </div>

        <div className="mt-3">
          <p className="tag-label text-[#a0703c] mb-2 flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5" /> Arrival chimes (per channel)</p>
          <div className="flex flex-wrap gap-2">
            {[["email", "Email"], ["sms", "SMS"], ["telegram", "Telegram"]].map(([k, label]) => (
              <button key={k} data-testid={`preview-arrival-${k}`} onClick={() => previewArrival(k)} className="flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 text-sm font-note font-bold text-[#2b2620] hover:bg-[#f3ead6]">
                <Play className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3">
          <p className="tag-label text-[#a0703c] mb-2 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Placing a note</p>
          <div className="space-y-2">
            {PLACE_SOUNDS.map((o) => (
              <OptionRow key={o.key} opt={o} selected={placeKey === o.key} onSelect={() => pickPlace(o.key)} onPreview={() => previewPlace(o.key)} testid={`place-sound-${o.key}`} />
            ))}
            {hasPlaceCustom && (
              <OptionRow opt={{ key: "custom", label: "My uploaded sound", hint: "your own audio file" }} selected={placeKey === "custom"} onSelect={() => pickPlace("custom")} onPreview={() => previewPlace("custom")} onDelete={() => removeCustom("place")} testid="place-sound-custom" />
            )}
          </div>
          <input ref={placeFileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => upload("place", e)} data-testid="place-sound-upload-input" />
          <button data-testid="place-sound-upload" onClick={() => placeFileRef.current?.click()} disabled={busy === "place"} className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#c9b48f] bg-white/60 hover:bg-white py-2.5 text-sm font-display font-bold text-[#5a5148]">
            <Upload className="w-4 h-4" /> {uploadLabel("place", hasPlaceCustom)}
          </button>
        </div>

        <div className="mt-5">
          <p className="tag-label text-[#a0703c] mb-2 flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Crumpling a note done</p>
          <div className="space-y-2">
            {REMOVE_SOUNDS.map((o) => (
              <OptionRow key={o.key} opt={o} selected={removeKey === o.key} onSelect={() => pickRemove(o.key)} onPreview={() => previewRemove(o.key)} testid={`remove-sound-${o.key}`} />
            ))}
            {hasRemoveCustom && (
              <OptionRow opt={{ key: "custom", label: "My uploaded sound", hint: "your own audio file" }} selected={removeKey === "custom"} onSelect={() => pickRemove("custom")} onPreview={() => previewRemove("custom")} onDelete={() => removeCustom("remove")} testid="remove-sound-custom" />
            )}
          </div>
          <input ref={removeFileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => upload("remove", e)} data-testid="remove-sound-upload-input" />
          <button data-testid="remove-sound-upload" onClick={() => removeFileRef.current?.click()} disabled={busy === "remove"} className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#c9b48f] bg-white/60 hover:bg-white py-2.5 text-sm font-display font-bold text-[#5a5148]">
            <Upload className="w-4 h-4" /> {uploadLabel("remove", hasRemoveCustom)}
          </button>
        </div>

        <p className="text-[11px] text-[#a89a86] mt-4 flex items-center gap-1"><Music4 className="w-3 h-3" /> mp3, wav, ogg, m4a or aac • up to 3MB</p>
      </DialogContent>
    </Dialog>
  );
}
